import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { LoggerService } from 'src/app/services/logger.service';
import { UserApiService } from '../../services/user-api.service';
import { PCTableOutputModel } from '../../models/pc-table-input-model';
import { ApiRequestModel } from 'src/app/models/api-request-model';
import {
  MatTreeFlatDataSource,
  MatTreeFlattener,
} from '@angular/material/tree';
import { FlatTreeControl } from '@angular/cdk/tree';

interface PermissionNode {
  viewName: string;
  icon: string;
  color: string;
  order: number;
  hasAccess: boolean;
  children?: PermissionNode[] | null;
}

interface FlatNode {
  expandable: boolean;
  viewName: string;
  icon: string;
  color: string;
  level: number;
  hasAccess: boolean;
}

@Component({
  selector: 'app-permission-modal',
  templateUrl: './permission-modal.component.html',
  styleUrl: './permission-modal.component.css',
})
export class PermissionModalComponent {
  loading = false;

  @Input() userId: string | undefined;
  @Output() parentAction = new EventEmitter<PCTableOutputModel>();
  @ViewChild('modalContent') modalContent!: any;

  constructor(
    public logger: LoggerService,
    private modalService: NgbModal,
    private apiService: UserApiService
  ) {}

  private _transformer = (node: PermissionNode, level: number): FlatNode => ({
    expandable: !!node.children && node.children.length > 0,
    viewName: node.viewName,
    icon: node.icon,
    color: node.color,
    level,
    hasAccess: node.hasAccess,
  });

  treeControl = new FlatTreeControl<FlatNode>(
    (node) => node.level,
    (node) => node.expandable
  );

  treeFlattener = new MatTreeFlattener(
    this._transformer,
    (node) => node.level,
    (node) => node.expandable,
    (node) => node.children
  );

  dataSource = new MatTreeFlatDataSource(this.treeControl, this.treeFlattener);

  hasChild = (_: number, node: FlatNode) => node.expandable;

  public openModal() {
    try {
      this.getData();
      this.modalService
        .open(this.modalContent, { ariaLabelledBy: 'modal-basic-title' })
        .result.then(
          () => {},
          () => {}
        );
    } catch (error) {
      this.logger.addException(error, false);
    }
  }

  public getData() {
    try {
      if (this.userId == null) {
        this.loading = false;
        return;
      }
      const url = `Permission/GetPermission?userId=${this.userId}`;
      if (!this.apiService.tokenValidation(new ApiRequestModel(url))) {
        return;
      }

      this.loading = true;
      this.apiService.getWitoutPage(new ApiRequestModel(url)).subscribe(
        (result) => {
          this.logger.addResponseLog(url, result, `getWithoutPage`);
          if (result.succeeded) {
            this.dataSource.data = result.data!;
          } else {
            this.logger.addBackEndMessage(result);
          }
          this.loading = false;
        },
        (error) => {
          this.apiService.AddErrorLog(error);
          this.loading = false;
        }
      );
    } catch (e) {
      this.logger.addException(e, true);
      this.loading = false;
    }
  }

  private syncFlatNodesToTree() {
    const flatNodes = this.treeControl.dataNodes;
    const sync = (nodes: PermissionNode[]) => {
      for (let node of nodes) {
        const flat = flatNodes.find((f) => f.viewName === node.viewName);
        if (flat) {
          node.hasAccess = flat.hasAccess;
        }
        if (node.children && node.children.length > 0) {
          sync(node.children);
        }
      }
    };
    sync(this.dataSource.data as PermissionNode[]);
  }

  public onSubmit() {
    console.log(this.dataSource);
    this.syncFlatNodesToTree();

    try {
      this.loading = true;

      var backendData = {
        permissions: this.dataSource.data, //TODO : here didnt get last changed data
        userId: this.userId,
      };

      const url = `Permission/SetPermission`;

      this.apiService.postItem(new ApiRequestModel(url, backendData)).subscribe(
        (result) => {
          if (result.succeeded) {
            this.modalService.dismissAll();
            this.parentAction.next(new PCTableOutputModel('SaveChange', null));
          } else {
            this.logger.addBackEndMessage(result);
          }
          this.loading = false;
        },
        (error) => {
          this.apiService.AddErrorLog(error);
          this.loading = false;
        },
        () => {
          this.loading = false;
          console.log(`Completed!`);
        }
      );
    } catch (e) {
      this.logger.addException(e, true);
      this.loading = false;
    }
  }

  public changeParentAccess(node: FlatNode) {
    const originalTreeData = this.dataSource.data as PermissionNode[];
    const allFlatNodes = this.treeControl.dataNodes;
    const parentIndex = allFlatNodes.findIndex(
      (n) => n.viewName === node.viewName
    );

    // 1. به‌روز رسانی درخت اصلی
    const updateNodeAccess = (
      nodes: PermissionNode[],
      targetViewName: string,
      newAccess: boolean
    ): boolean => {
      for (let item of nodes) {
        if (item.viewName === targetViewName) {
          item.hasAccess = newAccess;
          if (item.children) {
            item.children.forEach((child) =>
              setAccessRecursively(child, newAccess)
            );
          }
          return true;
        } else if (item.children) {
          if (updateNodeAccess(item.children, targetViewName, newAccess)) {
            return true;
          }
        }
      }
      return false;
    };

    const setAccessRecursively = (node: PermissionNode, value: boolean) => {
      node.hasAccess = value;
      if (node.children) {
        node.children.forEach((child) => setAccessRecursively(child, value));
      }
    };

    updateNodeAccess(originalTreeData, node.viewName, node.hasAccess);

    // 2. به‌روز رسانی FlatNode ها در UI
    for (
      let i = parentIndex + 1;
      i < allFlatNodes.length && allFlatNodes[i].level > node.level;
      i++
    ) {
      allFlatNodes[i].hasAccess = node.hasAccess;
    }
  }

  public changeChildAccess(node: FlatNode) {
    const allNodes = this.treeControl.dataNodes;
    let currentNode = node;

    while (currentNode.level > 0) {
      const currentIndex = allNodes.findIndex(
        (n) => n.viewName === currentNode.viewName
      );

      if (currentIndex === -1) break;

      let parent: FlatNode | undefined;
      for (let i = currentIndex - 1; i >= 0; i--) {
        if (allNodes[i].level === currentNode.level - 1) {
          parent = allNodes[i];
          break;
        }
      }

      if (!parent) break;

      const parentIndex = allNodes.findIndex(
        (n) => n.viewName === parent!.viewName
      );
      const children: FlatNode[] = [];

      for (
        let j = parentIndex + 1;
        j < allNodes.length && allNodes[j].level > parent!.level;
        j++
      ) {
        if (allNodes[j].level === parent!.level + 1) {
          children.push(allNodes[j]);
        }
      }

      const anyChecked = children.some((n) => n.hasAccess);
      parent.hasAccess = anyChecked;

      this.updateOriginalTreeAccess(parent.viewName, anyChecked);

      currentNode = parent;
    }
  }

  private updateOriginalTreeAccess(viewName: string, value: boolean) {
    const updateNode = (nodes: PermissionNode[]): boolean => {
      for (let node of nodes) {
        if (node.viewName === viewName) {
          node.hasAccess = value;
          return true;
        } else if (node.children) {
          if (updateNode(node.children)) return true;
        }
      }
      return false;
    };

    updateNode(this.dataSource.data as PermissionNode[]);
  }
}
